import { prop } from '@ipfabric/utils/fp/prop';
import { Dialog } from 'components/Dialog/Dialog';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { setSystemUnavailable } from 'components/GlobalState/commands/setSystemUnavailable';
import { constVoid } from 'fp-ts/lib/function';
import { type FC, memo, useEffect, useState } from 'react';
import { isNewVersionRunning, probeApi } from 'utils/request/systemUnavailable';
import { reloadPage, tryAutoReload } from './reloadPage';

const POLL_INTERVAL_MS = 5000;

const BareSystemUnavailableOverlay: FC = () => {
  const ctxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
    loadedVersion: prop('version'),
    unavailable: prop('systemUnavailable'),
  });
  // automatic reload was skipped by the reload loop guard, user has to reload manually
  const [reloadRequired, setReloadRequired] = useState(false);

  useEffect(() => {
    if (!ctxProps.unavailable) {
      return undefined;
    }
    let timeout: number;
    const poll = async () => {
      const result = await probeApi();
      if (!result.available) {
        timeout = window.setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }
      if (!isNewVersionRunning(result.version, ctxProps.loadedVersion)) {
        // same version (e.g. restart), the app keeps working without losing unsaved changes
        ctxProps.dispatch(setSystemUnavailable, { value: false });
        return;
      }
      // new version, reload fetches the fresh index.html with the new frontend
      if (!tryAutoReload()) {
        setReloadRequired(true);
      }
    };
    timeout = window.setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [ctxProps.unavailable, ctxProps.loadedVersion, ctxProps.dispatch]);

  if (!ctxProps.unavailable) {
    return null;
  }

  if (reloadRequired) {
    return (
      <Dialog
        headerMsg='IP Fabric is available again'
        primaryBtnMsg='Reload'
        onPrimaryClick={reloadPage}
        closable={false}
        onClose={constVoid}
      >
        Reload the page to continue working.
      </Dialog>
    );
  }

  return (
    <Dialog
      headerMsg='IP Fabric is temporarily unavailable'
      primaryBtnMsg='Reload now'
      onPrimaryClick={reloadPage}
      closable={false}
      onClose={constVoid}
    >
      <p className='flex items-center gap-2'>
        <i className='fa fa-spinner fa-spin' />
        The system is being upgraded or restarted.
      </p>
      <p className='m-0'>
        This page will continue automatically once IP Fabric is available again.
      </p>
    </Dialog>
  );
};

export const SystemUnavailableOverlay = memo(BareSystemUnavailableOverlay);
